import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { AdminEntityPicker, AdminFilterField, type AdminEntityKind } from "@/features/admin/tables";
import { selectProfileByEmail } from "@/features/profile/api/selectProfileByEmail";
import { useAuth } from "@/utils/auth";
import { useNav } from "@/utils/nav";
import Autocomplete from "@patient-watch/ui/autocomplete";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

export const Route = createFileRoute("/_app/design/components/search-fields")({
    component: SearchFieldsDemo,
});

function DemoSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
    return (
        <section className="space-y-4 pt-2 border-t">
            <div className="space-y-1">
                <h2 className="font-semibold text-lg">{title}</h2>
                <p className="text-muted-foreground text-sm">{description}</p>
            </div>
            <div className="space-y-6">{children}</div>
        </section>
    );
}

function Demo({ name, usage, value, children }: { name: string; usage: string; value: unknown; children: ReactNode }) {
    return (
        <div className="space-y-2">
            <div>
                <p className="font-medium text-sm">{name}</p>
                <p className="text-muted-foreground text-xs">{usage}</p>
            </div>
            {children}
            <pre className="bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded-md overflow-x-auto font-mono text-2xs text-slate-600 dark:text-slate-300">
                {JSON.stringify(value ?? null)}
            </pre>
        </div>
    );
}

const ENTITY_DEMOS: Array<{ entity: AdminEntityKind; label: string; usage: string }> = [
    { entity: "diary", label: "Diary", usage: "Search patient name or hospital number, or paste a diary UUID." },
    { entity: "user", label: "User", usage: "Search name or email, or paste a user UUID." },
    { entity: "organisation", label: "Organisation", usage: "Lists organisations straight away. Type to narrow." },
    { entity: "form", label: "Questionnaire", usage: "Lists questionnaires straight away. Type to narrow." },
    {
        entity: "input",
        label: "Input",
        usage: "Search input labels. Scoped to the questionnaire above when one is set.",
    },
    {
        entity: "entry",
        label: "Entry",
        usage: "Search answers or input labels. Each card shows the questionnaire, date and the answer that matched.",
    },
];

async function fetchEmailSuggestions(query: string) {
    const rows = await selectProfileByEmail(query);
    return (rows ?? []).map((row) => row.email).slice(0, 8);
}

function SearchFieldsDemo() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        { label: "Search", route: { to: "/design/components/search-fields" } },
    ]);

    const auth = useAuth();
    const isAdmin = Boolean(auth.roles?.isAdmin || auth.isSuperUser);
    const [email, setEmail] = useState("");
    const [entityValues, setEntityValues] = useState<Partial<Record<AdminEntityKind, string | null>>>({});

    return (
        <AdminPageLayout
            title="Search"
            description="Search bars that suggest matches as you type. Use these instead of building a new search, so search looks and behaves the same everywhere. For browsing a list before choosing, see Select dialogs."
            maxWidthClassName="max-w-2xl"
        >
            <div className="space-y-8">
                <DemoSection
                    title="Autocomplete"
                    description="A search bar with suggestions popping up underneath as you type. Arrow keys move through them and Enter picks one. Use when the value is plain text, such as an email address."
                >
                    <Demo name="Autocomplete" usage="Suggests user emails, as on Impersonate user." value={email}>
                        <Autocomplete
                            value={email}
                            onChange={setEmail}
                            fetchSuggestions={fetchEmailSuggestions}
                            placeholder="Search by email"
                        />
                    </Demo>
                </DemoSection>

                <DemoSection
                    title="Search the database"
                    description="A search box with results as cards underneath: type a name or paste a UUID, then pick a card. The picked record shows as a card with its full id, and Change or clear. Use for filters and for finding one record among thousands."
                >
                    {!isAdmin ? (
                        <p className="text-muted-foreground text-sm">
                            These search across every organisation, so results only show for admins.
                        </p>
                    ) : null}
                    {ENTITY_DEMOS.map(({ entity, label, usage }) => (
                        <Demo key={entity} name={`${label} picker`} usage={usage} value={entityValues[entity]}>
                            <AdminFilterField label={label} htmlFor={`design-search-${entity}`}>
                                <AdminEntityPicker
                                    id={`design-search-${entity}`}
                                    entity={entity}
                                    value={entityValues[entity] ?? null}
                                    scope={entity === "input" ? { formId: entityValues.form ?? null } : undefined}
                                    onChange={(value) => setEntityValues((prev) => ({ ...prev, [entity]: value }))}
                                />
                            </AdminFilterField>
                        </Demo>
                    ))}
                </DemoSection>
            </div>
        </AdminPageLayout>
    );
}
